/**
 * MOTOR DE RIEGO — geometría, cobertura, zonificación, canalización y bomba.
 * =============================================================================
 *
 * Es la primera rebanada vertical de la tesis del 2026-08-13: **la pericia vive en el
 * sistema, no en el operador.** Quien usa esto no tiene por qué saber hidráulica; su
 * trabajo es observar y medir. Traducir de lo observable a lo técnico es trabajo de
 * este archivo.
 *
 * Se eligió riego porque `areas/Sistemas/Simuladores_del_Cotizador.md` §3.1 lo declara
 * «factible al cien por ciento, sin condiciones»: es geometría pura en dos dimensiones.
 * Si la tesis falla aquí, falla barato.
 *
 * ---------------------------------------------------------------------------
 * QUÉ ES REAL Y QUÉ ES PROVISIONAL — leerlo antes de confiar en un número
 *
 * **Real y verificable**, marcado `fuente:` en cada función:
 *   · Hazen-Williams para pérdida por fricción, con C=150 para PVC liso.
 *   · Potencia hidráulica P = ρ·g·Q·H (la útil en el agua, sin dividir entre nada).
 *   · Toda la geometría (área, punto en polígono, distancia, árbol mínimo).
 *
 * **Cargado de fuente el 2026-08-14** — este módulo ya no tiene ningún `sinVerificar`:
 *   · El catálogo de boquillas, de la carta de rendimiento de Rain Bird.
 *   · El criterio de espaciamiento, con tres fuentes que coinciden. **Y estaba al
 *     revés**: se marcaba como defecto estar cerca, cuando lo recomendado es
 *     precisamente «cabeza con cabeza». Ver `ESPACIAMIENTO`.
 *   · La bomba, que dejó de calcularse con un rendimiento supuesto y ahora se
 *     **selecciona de la tabla de prestaciones del fabricante**. Ver `BOMBAS`.
 *
 * Lo único que ya no es un dato sino una **medición de campo** es la altura estática —el
 * desnivel entre la toma y el aspersor más alto—. Por eso su valor por defecto es 0 y no
 * un número inventado: si nadie la mide, el sistema no supone que existe.
 *
 * `PRODUCT.md` lo dice sin rodeos: sin estos datos el simulador dibuja pero **no
 * decide**, y sin decisión no hay foso. Ninguno se inventa en silencio.
 * ============================================================================= */

/* ============================ Geometría ============================ */
/* Vive en `geometria.js`, que es el sustrato común de TODOS los rubros —riego, cámaras,
   seguridad, iluminación, Wi-Fi y eléctrico comparten las mismas cuatro operaciones—.
   Se re-exporta desde aquí para no romper a quien ya importaba de `riego.js`, y porque
   quien lee este archivo espera encontrar el vocabulario completo del rubro. */
import {
  area, perimetro, dist, dentro, caja, lados, arbolMinimo, alcanza, cobertura
} from './geometria.js'

/* La costura con el catálogo de Juan. Ver la cabecera de ese archivo: el motor elige por
   física y cobra con lo que él tenga dado de alta. */
import { resolver, resolverProgramador, renglon } from './catalogo-tecnico.js'

export { area, perimetro, dist, dentro, caja, lados, cobertura }
/** ¿Este aspersor moja este punto? Respeta el arco: un sector, no siempre un círculo. */
export const cubrePunto = alcanza

/* ============================ Catálogo de aspersores ============================ */

/**
 * CATÁLOGO DE BOQUILLAS — datos reales de ficha de fabricante.
 *
 * `FUENTE:` carta de rendimiento oficial de la serie VAN de Rain Bird, publicada en
 * `rainbird.com` (`van-series-spray-nozzles-performance-charts.pdf`), lado métrico,
 * arco de 360° a 1.0 bar. Los rangos de radio salen de la lista de modelos de la misma
 * ficha.
 *
 * Rango de trabajo de la serie: **1.0 a 2.1 bar, óptimo 2.1 bar**. `radio` es el
 * alcance en el extremo BAJO de presión y `radioMax` en el óptimo; el sistema usa el
 * bajo, que es el conservador — si la casa tiene poca presión, el jardín igual queda
 * cubierto.
 *
 * **Ya NO son valores estimados.** Lo que sigue siendo decisión de Juan es *qué marca y
 * modelo va a distribuir*: si stockea otra, se sustituye esta tabla por su ficha. Esa es
 * una decisión comercial, no un dato técnico que él tenga que saber.
 *
 * `arco` en grados: 360 riega completo, 180 media luna contra un muro, 90 esquina.
 * El gasto escala con el arco, y eso está **confirmado en la propia carta**: la 12-VAN
 * da 5.80, 4.35, 2.90 y 1.45 l/min para 360°, 270°, 180° y 90° — exactamente
 * proporcional.
 */
export const ASPERSORES = [
  {
    id: 'van-10', nombre: 'Boquilla 10-VAN', modelo: 'Rain Bird VAN',
    radio: 2.1, radioMax: 3.0, gasto: 7.3, presion: 1.0, presionOptima: 2.1, arco: 360,
    para: 'Franjas angostas, camellones y jardineras.'
  },
  {
    id: 'van-12', nombre: 'Boquilla 12-VAN', modelo: 'Rain Bird VAN',
    radio: 2.7, radioMax: 3.7, gasto: 5.8, presion: 1.0, presionOptima: 2.1, arco: 360,
    para: 'Patios chicos y jardineras anchas.'
  },
  {
    id: 'van-15', nombre: 'Boquilla 15-VAN', modelo: 'Rain Bird VAN',
    radio: 3.4, radioMax: 4.6, gasto: 9.8, presion: 1.0, presionOptima: 2.1, arco: 360,
    para: 'Pasto en patios medianos. Es la más usada.'
  },
  {
    id: 'van-18', nombre: 'Boquilla 18-VAN', modelo: 'Rain Bird VAN',
    radio: 4.3, radioMax: 5.5, gasto: 15.9, presion: 1.0, presionOptima: 2.1, arco: 360,
    para: 'La mayor de la serie de abanico. Más allá, va rotor.'
  }
]

/**
 * NOTA SOBRE UNA ANOMALÍA APARENTE, que se registra en vez de suavizarla.
 *
 * La 10-VAN gasta **más** que la 12-VAN (7.3 contra 5.8 l/min) pese a tener menos
 * alcance. No es un error de transcripción: es lo que publica la carta, y es coherente
 * con la lámina que trae la misma tabla —96 mm/h para la 10-VAN contra 48 para la
 * 12-VAN—. Son orificios distintos: la chica moja menos superficie con más agua.
 *
 * Se deja tal cual. Si al pedir el material el proveedor da otro número, gana su ficha.
 */

export const aspersorPorId = id => ASPERSORES.find(a => a.id === id) || ASPERSORES[1]

/** Gasto real de una boquilla según su arco. Geometría, no dato de fabricante. */
export const gastoReal = asp => asp.gasto * ((asp.arco ?? 360) / 360)

/* ============================ Cobertura ============================ */
/* `cobertura` y `cubrePunto` viven en `geometria.js`: son idénticas para el aspersor,
   la cámara, el sensor de movimiento y la luminaria. Se re-exportan arriba. */

/**
 * ESPACIAMIENTO ENTRE ASPERSORES — el criterio de traslape.
 *
 * ⚠️ **CORREGIDO EL 2026-08-14, Y ESTABA AL REVÉS.** La primera versión marcaba como
 * defecto que dos aspersores estuvieran MÁS CERCA que un radio. Eso es exactamente la
 * condición recomendada, no un problema: la práctica de diseño se llama **«cabeza con
 * cabeza»** y consiste precisamente en que el chorro de cada aspersor alcance la base
 * del siguiente.
 *
 * El defecto real es el contrario: **espaciarlos de más.**
 *
 * `FUENTE:` tres independientes que coinciden.
 *   1. Rain Bird, carta de rendimiento de boquillas VAN, nota al pie de cada tabla:
 *      *«Square spacing based on 50% diameter of throw»* — es decir, separación igual
 *      al RADIO.
 *   2. Tesis de Zamorano «Diseño y cálculo de sistema de riego por aspersión»: el caso
 *      real diseña 46' × 50' con 94' de diámetro mojado, o sea 49% y 53% del diámetro.
 *   3. Literatura de diseño de riego: la separación no debe superar el **60% del
 *      diámetro mojado** en marco cuadrado o triangular.
 *
 * **Por qué existe la regla:** un aspersor no reparte parejo. Su lámina cae hacia el
 * borde del alcance, así que si se colocan justo a la distancia de su radio nominal sin
 * traslape, el anillo exterior queda mal regado aunque el dibujo se vea completo. El
 * traslape no es desperdicio: es lo que vuelve uniforme el riego.
 */
export const ESPACIAMIENTO = {
  /** Recomendado: cabeza con cabeza. Separación = radio = 50% del diámetro. */
  recomendado: 1.0,
  /** Máximo antes de perder uniformidad: 60% del diámetro = 1.2 radios. */
  maximo: 1.2,
  fuente: 'Rain Bird VAN performance chart · tesis Zamorano · literatura de diseño'
}

/**
 * Rain Bird: *«Radius reduction over 25% of the normal throw of the nozzle is not
 * recommended»*. Cerrar el tornillo de la boquilla más allá de eso deforma el patrón.
 */
export const REDUCCION_MAX_RADIO = 0.25

/**
 * Revisa el espaciamiento de cada par de aspersores vecinos.
 *
 * Devuelve solo los pares **separados de más** —que son los que dejan franja seca entre
 * ellos aunque sus círculos parezcan tocarse en el dibujo—. Estar más cerca del radio no
 * se reporta: cuesta más piezas, pero riega mejor.
 */
export function espaciamiento (aspersores) {
  const problemas = []
  for (let i = 0; i < aspersores.length; i++) {
    for (let j = i + 1; j < aspersores.length; j++) {
      const a = aspersores[i], b = aspersores[j]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      const rMenor = Math.min(a.radio, b.radio)
      // Solo interesan los que se pretenden vecinos: más allá de dos radios ya no
      // comparten frontera y su separación no es un defecto de traslape.
      if (d > rMenor * 2) continue
      const limite = rMenor * ESPACIAMIENTO.maximo
      if (d > limite) {
        problemas.push({
          a: i, b: j, distancia: d, limite,
          porque: `Están a ${d.toFixed(1)} m y el máximo con traslape suficiente es ` +
            `${limite.toFixed(1)} m (60% del diámetro). Entre los dos va a quedar una ` +
            'franja mal regada aunque los círculos se toquen.'
        })
      }
    }
  }
  return problemas
}

/** Nombre anterior, conservado para no romper a quien lo importaba. */
export const traslapes = espaciamiento

/* ============================ Zonificación ============================ */

/**
 * Reparte los aspersores en zonas que el caudal disponible pueda alimentar a la vez.
 *
 * **Esta es la regla que vuelve esto ingeniería y no dibujo**, y así lo dice el
 * documento de simuladores: el caudal de la toma limita cuántos aspersores riegan
 * simultáneamente, y de ahí salen las zonas y el número de válvulas.
 *
 * El reparto es voraz por cercanía: se empieza por el aspersor más lejano de la toma y
 * se van sumando los más próximos mientras quepan en el caudal. Agrupar por cercanía
 * importa porque una zona dispersa obliga a más tubería.
 *
 * `caudalDisponible` en L/min, medido en sitio. `margen` deja una reserva para no
 * trabajar al límite de la toma.
 */
export function zonificar (aspersores, caudalDisponible, margen = 0.9) {
  if (!aspersores.length) return []
  const util = caudalDisponible * margen
  const libres = aspersores.map((a, i) => ({ ...a, i }))
  const zonas = []

  while (libres.length) {
    // Semilla: el primero que quede. Después se agrega siempre el más cercano al grupo.
    const zona = [libres.shift()]
    let gasto = gastoReal(zona[0])
    let creció = true
    while (creció && libres.length) {
      creció = false
      let mejor = -1, mejorD = Infinity
      for (let k = 0; k < libres.length; k++) {
        if (gasto + gastoReal(libres[k]) > util) continue
        const d = Math.min(...zona.map(z => Math.hypot(z.x - libres[k].x, z.y - libres[k].y)))
        if (d < mejorD) { mejorD = d; mejor = k }
      }
      if (mejor >= 0) {
        gasto += gastoReal(libres[mejor])
        zona.push(libres.splice(mejor, 1)[0])
        creció = true
      }
    }
    zonas.push({ aspersores: zona, gasto, indices: zona.map(z => z.i) })
  }
  return zonas
}

/* ============================ Canalización ============================ */

/**
 * Rutea la tubería como árbol mínimo desde la toma, con tramos ortogonales.
 *
 * Árbol de expansión mínima (Prim) sobre la distancia MANHATTAN, no la euclidiana,
 * porque la zanja se excava en ángulo recto: dos tramos, uno horizontal y otro
 * vertical. Rutear con distancia en línea recta y luego dibujar en ángulo recto
 * subestima los metros de tubo, y los metros de tubo son dinero.
 *
 * Devuelve tramos, longitud total, y las piezas que se derivan de la forma del árbol:
 *   · un CODO por cada quiebre de noventa grados,
 *   · una TE por cada nodo del que salen dos o más ramas.
 */
export function canalizar (toma, aspersores) {
  // Delega en el árbol mínimo común: es la MISMA operación que llevar cable desde el
  // centro de carga o UTP desde el grabador. Solo cambian el origen y el nombre de las
  // piezas que se derivan de su forma.
  return arbolMinimo(toma, aspersores)
}

/* ============================ Hidráulica ============================ */

/**
 * Pérdida de carga por fricción — **Hazen-Williams**, la correlación estándar para agua
 * a temperatura ambiente en tubería a presión.
 *
 *   hf = 10.67 · L · Q^1.852 / ( C^1.852 · D^4.87 )
 *
 * con L y D en metros, Q en m³/s, y hf en metros de columna de agua.
 * `fuente:` C = 150 es el coeficiente estándar para PVC liso.
 *
 * @param metros longitud del tramo, m
 * @param litrosMin gasto, L/min
 * @param diametroMm diámetro interior, mm
 */
export function perdidaFriccion (metros, litrosMin, diametroMm, C = 150) {
  if (!metros || !litrosMin || !diametroMm) return 0
  const Q = litrosMin / 60000        // L/min -> m³/s
  const D = diametroMm / 1000        // mm -> m
  return (10.67 * metros * Math.pow(Q, 1.852)) / (Math.pow(C, 1.852) * Math.pow(D, 4.87))
}

/** Diámetros comerciales de PVC hidráulico, con su interior aproximado en mm. */
export const DIAMETROS = [
  { pulgadas: '1/2"', interior: 15.5 },
  { pulgadas: '3/4"', interior: 20.9 },
  { pulgadas: '1"',   interior: 26.6 },
  { pulgadas: '1 1/4"', interior: 35.0 },
  { pulgadas: '1 1/2"', interior: 40.9 }
]

/**
 * Elige el diámetro más chico cuya VELOCIDAD del agua se mantenga bajo el límite.
 *
 * `fuente:` el criterio de diseño habitual en riego es no pasar de ~1.5 m/s en la
 * línea principal: por encima aparecen ruido, erosión y sobre todo golpe de ariete al
 * cerrar una válvula. Velocidad v = Q / A.
 */
export const VELOCIDAD_MAX = 1.5   // m/s

export function diametroRecomendado (litrosMin) {
  const Q = litrosMin / 60000
  for (const d of DIAMETROS) {
    const A = Math.PI * Math.pow(d.interior / 2000, 2)   // m²
    if (Q / A <= VELOCIDAD_MAX) return { ...d, velocidad: Q / A }
  }
  const u = DIAMETROS[DIAMETROS.length - 1]
  const A = Math.PI * Math.pow(u.interior / 2000, 2)
  return { ...u, velocidad: Q / A, insuficiente: true }
}

/* ============================ Bomba ============================ */

/**
 * CURVAS DE BOMBA — tabla de prestaciones, no un rendimiento supuesto.
 *
 * ⚠️ **CAMBIO DE MÉTODO EL 2026-08-14.** La versión anterior calculaba la potencia
 * hidráulica y la dividía entre un rendimiento estimado de 0.6 para llegar a los HP.
 * Ese 0.6 era el último `sinVerificar` del módulo, y **no hacía falta**: el fabricante
 * publica directamente cuánta altura da cada modelo a cada caudal. Se selecciona de la
 * tabla, que es como se hace en la práctica.
 *
 * El rendimiento no desapareció —sigue existiendo dentro de la bomba—, pero ya no es un
 * dato de entrada que alguien tenga que adivinar: ahora se **deriva** del punto de
 * trabajo elegido y se reporta, que es exactamente al revés.
 *
 * `FUENTE:` Pedrollo, hoja técnica de la serie CP a 60 Hz
 * (`pedrollo.com/wp-content/uploads/schede-tecniche/EN/CP_EN-datasheet_60Hz.pdf`),
 * tabla «CURVES AND PERFORMANCE DATA — HS = 0 m». Los valores son la altura manométrica
 * total H en metros a cada caudal en l/min. La propia hoja declara que las curvas cumplen
 * la tolerancia grado 3B de EN ISO 9906.
 *
 * Se eligió la serie CP porque es de uso doméstico, se consigue en México y su hoja está
 * publicada completa. **Si Juan decide distribuir otra marca, se sustituye esta tabla por
 * su hoja** — la función de selección no cambia. Esa es decisión comercial, no técnica.
 *
 * El límite de succión de la serie es de 7 m; se declara aquí porque una toma más abajo
 * de eso ya no es problema de potencia sino de tipo de bomba.
 */
export const CAUDALES_CURVA = [0, 10, 20, 40, 60, 80, 90, 100, 110, 120, 130, 150, 160]

export const BOMBAS = [
  { id: 'cpm-600', modelo: 'Pedrollo CPm 600', hp: 0.50, kw: 0.37, amperes220: 2.7,
    curva: [22, 21, 20, 18, 15, 11] },
  { id: 'cpm-610', modelo: 'Pedrollo CPm 610', hp: 0.85, kw: 0.60, amperes220: 4.0,
    curva: [28.5, 28, 27, 25, 22, 19, 17] },
  { id: 'cpm-620', modelo: 'Pedrollo CPm 620', hp: 1.0, kw: 0.75, amperes220: 6.0,
    curva: [35, 34, 33, 31, 28.5, 25.5, 23.5, 21] },
  { id: 'cpm-650', modelo: 'Pedrollo CPm 650', hp: 1.5, kw: 1.1, amperes220: 8.8,
    curva: [40, null, 39, 38, 36, 33.1, 31.2, 28.8, 26] },
  { id: 'cpm-660', modelo: 'Pedrollo CPm 660', hp: 2.0, kw: 1.5, amperes220: 9.7,
    curva: [48, null, 47.5, 46, 44, 41, 39.5, 37.5, 35, 32] },
  { id: 'cpm-670', modelo: 'Pedrollo CPm 670', hp: 3.0, kw: 2.2, amperes220: 12.0,
    curva: [56, null, 55.5, 54.5, 52.5, 50, 48.5, 46.5, 44, 41.5, 38] }
]

export const SUCCION_MAX = 7

/**
 * Altura que da una bomba al caudal pedido, interpolando linealmente entre los dos puntos
 * publicados que lo rodean. Devuelve `null` si el caudal cae fuera de su curva —una bomba
 * no entrega más allá del último punto que su fabricante publica—.
 */
export function alturaEn (bomba, litrosMin) {
  const c = bomba.curva
  for (let i = 0; i < c.length - 1; i++) {
    if (c[i] === null || c[i + 1] === null) continue
    const q0 = CAUDALES_CURVA[i], q1 = CAUDALES_CURVA[i + 1]
    if (litrosMin < q0 || litrosMin > q1) continue
    const t = (litrosMin - q0) / (q1 - q0)
    return c[i] + t * (c[i + 1] - c[i])
  }
  return null
}

/**
 * La bomba más chica cuya curva pasa por encima del punto de trabajo.
 *
 * No se «redondea al siguiente HP comercial»: se busca en la tabla. Una bomba de 1 HP no
 * sirve para cualquier punto de 1 HP —sirve para los puntos que están debajo de SU curva—,
 * y esa distinción es justo la que un operador no experto no puede hacer de cabeza.
 */
export function seleccionarBomba (litrosMin, alturaRequerida) {
  for (const b of BOMBAS) {
    const h = alturaEn(b, litrosMin)
    if (h !== null && h >= alturaRequerida) {
      return { ...b, alturaDisponible: h, holgura: h - alturaRequerida, qTrabajo: litrosMin }
    }
  }
  return null
}

export const MCA_POR_BAR = 10.197

/**
 * Dimensiona la bomba.
 *
 * Altura manométrica total = altura estática + fricción + accesorios + presión que exige
 * el aspersor. La presión del aspersor viene en bar y se convierte a metros de columna de
 * agua: `fuente:` 1 bar = 10.197 mca.
 *
 * `alturaEstatica` es un **dato de campo**, no una constante: es el desnivel entre la
 * toma y el aspersor más alto. Por defecto 0 —jardín al mismo nivel que la llave, que es
 * el caso común— y se sobrescribe con lo que se mida.
 *
 * La potencia hidráulica P = ρ·g·Q·H sigue calculándose porque es física exacta, pero ya
 * no manda: sirve para **derivar** el rendimiento del punto de trabajo elegido. Si sale
 * un rendimiento absurdo, es señal de que la selección quedó muy holgada.
 */
export function bomba ({ litrosMin, metrosTuberia, diametroMm, presionAspersorBar, alturaEstatica = 0 }) {
  const hf = perdidaFriccion(metrosTuberia, litrosMin, diametroMm)
  // Accesorios: se estiman como una fracción de la fricción del tubo recto. Es la
  // práctica habitual cuando no se cuenta pieza por pieza; queda declarado como estimado.
  const hAccesorios = hf * 0.25
  const hAspersor = presionAspersorBar * MCA_POR_BAR
  const H = alturaEstatica + hf + hAccesorios + hAspersor

  const Q = litrosMin / 60000                       // m³/s
  const hidraulica = 1000 * 9.81 * Q * H            // W, potencia útil en el agua

  const elegida = seleccionarBomba(litrosMin, H)

  return {
    alturaTotal: H,
    friccion: hf,
    accesorios: hAccesorios,
    presionAspersor: hAspersor,
    alturaEstatica,
    watts: hidraulica,
    /** El modelo concreto, con su curva. `null` si ninguna de la serie llega. */
    bomba: elegida,
    hpComercial: elegida ? elegida.hp : null,
    /** Rendimiento del conjunto en ESTE punto. Derivado, no supuesto. */
    rendimiento: elegida ? hidraulica / (elegida.kw * 1000) : null,
    /** Ninguna bomba de la serie llega: hay que partir el riego en más zonas. */
    fueraDeSerie: !elegida
  }
}

/* ============================ Cálculo completo ============================ */

/**
 * La cadena entera, de la geometría al material. Es lo que se ejecuta al tocar
 * «calcular»: el operador no ve ninguna de las fórmulas de arriba, ve el resultado y —si
 * lo pide— de qué medición y de qué regla salió cada número. Principio 9 de PRODUCT.md:
 * toda decisión automática es auditable.
 */
export function calcular ({ poligono, aspersores, toma, caudalDisponible, alturaEstatica = 0,
                            insumos = [], holguraObra = 1.10 }) {
  const cob = cobertura(poligono, aspersores)
  const zonas = zonificar(aspersores, caudalDisponible)
  const red = canalizar(toma, aspersores)

  // La red se dimensiona para la zona que más gasta: es la que va a correr a la vez.
  const gastoPico = zonas.length ? Math.max(...zonas.map(z => z.gasto)) : 0
  const diam = diametroRecomendado(gastoPico)
  const presionMax = aspersores.length
    ? Math.max(...aspersores.map(a => a.presion || 2.1))
    : 2.1

  const b = gastoPico
    ? bomba({
        litrosMin: gastoPico,
        metrosTuberia: red.metros,
        diametroMm: diam.interior,
        presionAspersorBar: presionMax,
        alturaEstatica
      })
    : null

  return {
    areaJardin: area(poligono),
    perimetro: perimetro(poligono),
    cobertura: cob,
    espaciamiento: espaciamiento(aspersores),
    traslapes: espaciamiento(aspersores),   // nombre anterior, para no romper a quien lo lea
    zonas,
    red,
    diametro: diam,
    gastoPico,
    bomba: b,
    materiales: materiales({ red, zonas, aspersores, diam, bomba: b, insumos, holguraObra })
  }
}

/**
 * Lista de materiales derivada de la geometría, **cobrada con el catálogo de Juan**.
 *
 * Cada renglón separa dos cosas que antes venían mezcladas:
 *   · `de`     — por qué esa CANTIDAD. Sale del plano y de la hidráulica. No es editable.
 *   · `porque` — por qué ese PRODUCTO. Sale del catálogo. Juan lo mueve cuando quiera.
 *
 * `insumos` es la lista viva del almacén. Si no se pasa, los renglones salen igual con su
 * cantidad y su razón técnica, pero marcados `falta`: sirve para ver el diseño sin tener
 * catálogo, y **nunca inventa un precio**.
 */
export function materiales ({ red, zonas, aspersores, diam, bomba: b, insumos = [], holguraObra = 1.10 }) {
  const lista = []

  const porModelo = {}
  for (const a of aspersores) porModelo[a.modelo] = (porModelo[a.modelo] || 0) + 1

  for (const [modelo, cantidad] of Object.entries(porModelo)) {
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'boquilla', boquilla: modelo }),
      cantidad, unidad: 'pieza',
      de: 'Uno por cada aspersor colocado en el plano'
    }))
  }

  // El cuerpo emergente va aparte de la boquilla porque se compran por separado y se
  // combinan: la misma base admite cualquiera de las cuatro boquillas.
  if (aspersores.length) lista.push(renglon({
    insumo: resolver(insumos, { clase: 'cuerpo-aspersor' }),
    cantidad: aspersores.length, unidad: 'pieza',
    de: 'Uno por boquilla. Base y boquilla se compran por separado'
  }))

  /* La holgura de obra —cortes, desperdicio, recorridos que el plano no ve— es práctica
     de instalación, no norma. Por eso llega como parámetro editable y no como constante:
     el taller de Juan puede desperdiciar más o menos que el promedio. */
  const pct = Math.round((holguraObra - 1) * 100)
  lista.push(renglon({
    insumo: resolver(insumos, { clase: 'tubo-hidraulico', pulgadas: diam.pulgadas }),
    cantidad: Math.ceil(red.metros * holguraObra), unidad: 'm',
    de: `Árbol mínimo desde la toma (${red.metros.toFixed(1)} m) más ${pct}% de holgura de obra`
  }))
  if (red.codos) lista.push(renglon({
    insumo: resolver(insumos, { clase: 'codo', pulgadas: diam.pulgadas }),
    cantidad: red.codos, unidad: 'pieza',
    de: 'Uno por cada quiebre de la zanja'
  }))
  if (red.tes) lista.push(renglon({
    insumo: resolver(insumos, { clase: 'te', pulgadas: diam.pulgadas }),
    cantidad: red.tes, unidad: 'pieza',
    de: 'Una por cada nodo del que sale más de una rama'
  }))
  if (zonas.length) {
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'valvula-zona' }),
      cantidad: zonas.length, unidad: 'pieza',
      de: 'Una por zona. Las zonas salen del caudal disponible, no del gusto'
    }))
    lista.push(renglon({
      insumo: resolverProgramador(insumos, zonas.length),
      cantidad: 1, unidad: 'pieza',
      de: `Una estación por válvula, y son ${zonas.length} zonas`
    }))
  }
  // La bomba se resuelve por su `id` de modelo, no por caballaje: el caballaje solo no
  // basta para comprar —dos bombas del mismo HP tienen curvas distintas— y el punto de
  // trabajo del que salió es lo que hay que poder auditar.
  if (b && b.bomba) lista.push(renglon({
    insumo: resolver(insumos, { clase: 'bomba', bomba: b.bomba.id }),
    cantidad: 1, unidad: 'pieza',
    de: `Su curva da ${b.bomba.alturaDisponible.toFixed(1)} m a ${Math.round(b.bomba.qTrabajo)} L/min, ` +
        `y el sistema pide ${b.alturaTotal.toFixed(1)} m`
  }))
  return lista
}
